import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import api from "../api";
import { useAuth } from "../AuthContext";
import { ClockCounterClockwise, CaretRight } from "@phosphor-icons/react";

// Compact "last 10 movements" panel for the distributor dashboard.
// The /movement-log endpoint is already RBAC-scoped: distributors only ever
// receive rows where they are the sender or the recipient.
export function DistributorRecentActivity() {
  const { t, i18n } = useTranslation();
  const { user } = useAuth();
  const [items, setItems] = useState(null);

  useEffect(() => {
    api.get("/movement-log", { params: { limit: 10 } })
      .then(({ data }) => setItems(
        (data || [])
          .filter((m) => !m.cancelled && m.product_name && Number(m.quantity))
          .slice(0, 10)
      ))
      .catch(() => setItems([]));
  }, []);

  if (!user || items === null) return null;

  const classify = (m) => {
    if (m.kind === "adjust" || m.action === "adjust") return "act_adjusted";
    if (m.kind === "return" || m.transfer_type === "return" || m.action === "return") return "act_returned";
    if (m.to_user_id === user.id) return "act_received";
    if (m.from_user_id === user.id) return m.transfer_type === "sale" ? "act_sold" : "act_gave";
    return "act_adjusted";
  };
  const BADGE = {
    act_received: "bg-[#fbf6e7] text-[#8a6a1c] border-[#C9A84C]/60",
    act_sold: "bg-emerald-50 text-emerald-800 border-emerald-300",
    act_gave: "bg-zinc-50 text-zinc-700 border-zinc-300",
    act_returned: "bg-blue-50 text-blue-800 border-blue-300",
    act_adjusted: "bg-zinc-50 text-zinc-700 border-zinc-300",
  };
  const fmtWhen = (iso) => {
    try {
      return new Date(iso).toLocaleDateString(i18n.language === "ru" ? "ru-RU" : "en-US", {
        month: "short", day: "numeric",
      });
    } catch { return ""; }
  };

  return (
    <div className="bg-white border-2 border-zinc-950 rounded-xl p-4 sm:p-5" data-testid="dist-recent-activity">
      <div className="flex items-center justify-between gap-2 mb-3">
        <h3 className="font-semibold text-base flex items-center gap-2">
          <ClockCounterClockwise size={18} weight="bold" className="text-[#8a6a1c]" />
          {t("dist_portal.recent_activity")}
        </h3>
        <Link
          to="/movement-log"
          className="text-xs font-semibold text-[#8a6a1c] hover:underline inline-flex items-center gap-0.5"
          data-testid="dist-view-all-activity"
        >
          {t("dist_portal.view_all")} <CaretRight size={12} weight="bold" />
        </Link>
      </div>
      {items.length === 0 ? (
        <p className="text-sm text-zinc-500" data-testid="dist-no-activity">{t("dist_portal.no_activity")}</p>
      ) : (
        <div className="divide-y divide-zinc-100">
          {items.map((m) => {
            const key = classify(m);
            return (
              <div key={m.id} className="py-2 flex flex-wrap items-center gap-x-3 gap-y-1" data-testid={`dist-activity-row-${m.id}`}>
                <span className="text-[11px] text-zinc-500 tabular-nums w-12 shrink-0">{fmtWhen(m.created_at)}</span>
                <span className={`text-[10px] font-semibold uppercase tracking-wide px-2 py-0.5 rounded-full border ${BADGE[key]}`}>
                  {t(`dist_portal.${key}`)}
                </span>
                <span className="text-sm font-medium text-zinc-900 min-w-0 truncate flex-1">
                  {m.product_name || "—"}
                </span>
                <span className="text-sm font-semibold tabular-nums shrink-0">
                  ×{Math.abs(Number(m.quantity) || 0)}
                </span>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
